// v6 F42k v14g — AI Offerte Assistent panel (Emma).
// Twee flows:
//  1. Genereer projectomschrijving → LLM produceert {title, description,
//     works_included, remarks}. User kan per-veld of allemaal accepteren.
//     Accept schrijft naar quote + pusht undo-entry (via onAcceptField).
//  2. Controleer offerte → LLM retourneert waarschuwingen. Read-only.
//
// Extra context: compact optioneel tekstveld dat aan beide flows meegaat.
// LLM krijgt strikte "verzin niets" prompt (zie backend ai_assistant.py).
import React, { useState } from "react";
import { Sparkles, RefreshCw, X, Check, ChevronDown, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// iter113 — Q-flag: 'Emma kijkt mee' tijdelijk verborgen uit user-flow
// wegens resterende false positives ondanks iter110/111/112 semantische
// fixes. Backend endpoint (/api/quotes/{qid}/ai-check), check_quote(),
// _work_semantically_present() en de volledige TLL-post-filter blijven
// intact in source voor later hergebruik. Flip naar true om de flow
// direct te reactiveren zonder verdere code-wijzigingen.
const SHOW_EMMA_CHECK_UI = false;

// v14i — Warme, menselijke labels voor "Emma kijkt mee". Machine-schema
// (type/severity) blijft ongewijzigd — dit is enkel presentatielaag.
const SEVERITY_STYLE = {
  high: "bg-red-50 text-red-800 border-red-200",
  warn: "bg-amber-50 text-amber-800 border-amber-200",
  info: "bg-sky-50 text-sky-800 border-sky-200",
};
const SEVERITY_LABEL = {
  high: "Belangrijk om even te bekijken",
  warn: "Kleine bedenking",
  info: "Handig om te weten",
};
const TYPE_LABEL = {
  missing_material: "Materiaal om nog te bevestigen",
  contradiction: "Kleine tegenspraak",
  works_missing_in_text: "Werk dat nog niet in je tekst staat",
  text_extra_works: "Tekst noemt iets dat niet geselecteerd is",
  missing_exclusion: "Misschien nuttig om te vermelden",
};

// v14m — Labels voor target_field zodat de preview-dialog helder toont
// WAAR de suggestie wordt toegevoegd.
const FIELD_LABEL = {
  title: "Offertetitel",
  description: "Korte omschrijving",
  works_included: "De werken omvatten",
  remarks: "Opmerkingen / uitsluitingen",
};

// v14m — Idempotency helper: is `suggestion` reeds aanwezig in `current`?
// Normaliseer whitespace + lowercase voor robuuste vergelijking.
function _containsSuggestion(current, suggestion) {
  if (!suggestion) return false;
  const norm = (s) => String(s || "").replace(/\s+/g, " ").trim().toLowerCase();
  const c = norm(current);
  const s = norm(suggestion);
  if (!s) return false;
  return c.includes(s);
}

async function _post(url, body) {
  const r = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

/**
 * @param quoteId          — huidige quote.id
 * @param currentProjectName — project.name (voor "Ook projectnaam bijwerken?")
 * @param onAcceptField    — (field: title|description|works_included|remarks, value: string) => void
 *                            De caller schrijft de waarde weg + pusht undo-entry.
 * @param onUpdateProjectName — (name: string) => Promise<void>  (optioneel, alleen bij AI-titel accept met bevestiging)
 * @param changeFingerprint — string die verandert wanneer relevante offerte-data wijzigt
 *                            (ruimtes, werkposten, tekstvelden). Wordt lokaal vergeleken
 *                            met de fingerprint op moment van laatste AI-generatie.
 *                            Geen automatische regeneratie — enkel subtiele notice.
 * @param currentFields     — v14m — {description, works_included, remarks, title} van huidige
 *                            quote. Nodig om te controleren of suggested_text al aanwezig is
 *                            (dubbele toevoeging blokkeren) en om de preview 'huidig veld'
 *                            correct in te vullen.
 */
export default function AIAssistantPanel({
  quoteId,
  currentProjectName = "",
  onAcceptField,
  onUpdateProjectName,
  changeFingerprint = "",
  currentFields = {},
}) {
  const [open, setOpen] = useState(false);
  const [extraContext, setExtraContext] = useState("");
  const [busyGen, setBusyGen] = useState(false);
  const [busyCheck, setBusyCheck] = useState(false);
  const [gen, setGen] = useState(null);
  const [genErr, setGenErr] = useState("");
  const [chk, setChk] = useState(null);
  const [chkErr, setChkErr] = useState("");
  // Per-veld accepted state (om UI-status te tonen)
  const [accepted, setAccepted] = useState({});
  // v14m — per-warning applied state (idx → true).
  const [appliedSug, setAppliedSug] = useState({});
  // v14m — preview-dialog voor "Overnemen": {idx, warning} of null.
  const [previewSug, setPreviewSug] = useState(null);
  // v14g — Fingerprint bij laatste succesvolle generate.
  const [genFingerprint, setGenFingerprint] = useState(null);
  const isStale = genFingerprint != null && changeFingerprint !== genFingerprint;

  const doGenerate = async () => {
    setBusyGen(true); setGenErr(""); setAccepted({});
    try {
      const r = await _post(`${API}/quotes/${quoteId}/ai-generate-description`,
        { extra_context: extraContext });
      if (r.error) throw new Error(r.error);
      setGen(r);
      // v14g — snapshot fingerprint na succesvolle generate. Volgende
      // fingerprint-mismatch triggert enkel een subtiele notice, niet
      // een automatische regeneratie.
      setGenFingerprint(changeFingerprint);
    } catch (e) {
      setGenErr(e.message || "Onbekende fout");
    } finally { setBusyGen(false); }
  };

  // v14m.2 — STRICT: doCheck haalt warnings op maar RAAKT NOOIT `gen`.
  // De tijdelijke werkversie blijft exact zoals ze was; suggesties reizen alleen
  // naar `gen` via applySuggestion, dat vereist een expliciete confirm-klik.
  const doCheck = async () => {
    setBusyCheck(true); setChkErr(""); setAppliedSug({});
    try {
      const r = await _post(`${API}/quotes/${quoteId}/ai-check`,
        { extra_context: extraContext });
      if (r.error) throw new Error(r.error);
      setChk(r);
      // NB: geen setGen() hier. Emma kijkt mee is enkel observatie.
    } catch (e) {
      setChkErr(e.message || "Onbekende fout");
    } finally { setBusyCheck(false); }
  };

  // v14m.1 — P0 workflow-fix: Emma's suggestie MAG NIET direct naar de
  // echte quote schrijven. In plaats daarvan updaten we uitsluitend de
  // lokale `gen`-preview (draft). De echte offerte-velden wijzigen pas
  // wanneer de gebruiker expliciet 'Overnemen per veld' of 'Accepteer alles'
  // in de AI-preview klikt (bestaande flow via onAcceptField).
  //
  // Als er nog geen `gen`-preview bestaat (user startte niet met Generate),
  // seeden we `gen` uit `currentFields` — de huidige quote-velden — zodat
  // de gebruiker meteen ziet dat er een tijdelijke werkversie ontstaat. De echte
  // offerte blijft ongewijzigd totdat er expliciet geaccepteerd wordt.
  const applySuggestion = (idx, warning) => {
    if (!warning || !warning.suggested_text || !warning.target_field) return;
    const field = warning.target_field;
    const mode = warning.apply_mode || "append";
    const sugg = String(warning.suggested_text || "").trim();
    if (!sugg) return;

    // Bepaal de draft-basis: bestaande gen-preview, of seed uit currentFields.
    const genNow = gen || {
      title: String(currentFields?.title || ""),
      description: String(currentFields?.description || ""),
      works_included: String(currentFields?.works_included || ""),
      remarks: String(currentFields?.remarks || ""),
      _seeded_from_quote: true,  // markeer als 'draft uit huidige quote'
    };
    const currentDraft = String(genNow[field] || "").trim();

    // Idempotent: als de suggestie letterlijk in de draft (of in de echte
    // quote-veld wanneer er nog geen draft was) staat, markeer alleen toegepast.
    if (_containsSuggestion(currentDraft, sugg) ||
        _containsSuggestion(currentFields?.[field] || "", sugg)) {
      setAppliedSug((s) => ({ ...s, [idx]: true }));
      setPreviewSug(null);
      return;
    }

    let next;
    if (mode === "replace" || !currentDraft) {
      next = sugg;
    } else {
      next = `${currentDraft}\n\n${sugg}`;
    }
    // Update ALLEEN de lokale draft. GEEN onAcceptField-aanroep.
    setGen({ ...genNow, [field]: next });
    setAppliedSug((s) => ({ ...s, [idx]: true }));
    setPreviewSug(null);
  };

  const acceptField = (field) => {
    if (!gen || !onAcceptField) return;
    onAcceptField(field, gen[field] || "");
    setAccepted((s) => ({ ...s, [field]: true }));
    // v14g — bij accept van AI-titel: optioneel projectnaam ook bijwerken.
    if (field === "title" && onUpdateProjectName && gen.title) {
      const cur = (currentProjectName || "").trim();
      const next = (gen.title || "").trim();
      if (cur !== next) {
        if (window.confirm(`Wil je ook de Projectnaam bijwerken naar:\n\n"${next}"\n\n(Huidige: "${cur || "(leeg)"}")`)) {
          onUpdateProjectName(next);
        }
      }
    }
  };

  const acceptAll = () => {
    ["title", "description", "works_included", "remarks"].forEach((f) => {
      if (!gen || !gen[f]) return;
      // v14m.1 — Bij een van-quote-geseede draft: alleen velden accepteren
      // die daadwerkelijk verschillen van de huidige quote-waarde.
      if (gen._seeded_from_quote) {
        const cur = String(currentFields?.[f] ?? "").trim();
        const draft = String(gen[f] || "").trim();
        if (cur === draft) return;
      }
      acceptField(f);
    });
  };

  return (
    <div className="border border-[#E5E2DC] rounded-2xl bg-[#FFFBF5] mb-4" data-testid="ai-assistant-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
        data-testid="ai-assistant-toggle"
      >
        <ChevronDown className={`w-4 h-4 text-[#B07A4F] transition-transform ${open ? "" : "-rotate-90"}`} />
        <Sparkles className="w-5 h-5 text-[#B07A4F]" />
        <span className="font-heading text-base font-semibold flex-1">
          Emma — AI Offerte Assistent
        </span>
        <span className="text-[10px] uppercase tracking-wider text-[#A8A7A0] hidden sm:inline">
          Genereer omschrijving · Controleer offerte
        </span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-[#F0EFEA]">
          {/* Extra context */}
          <div className="space-y-1.5 pt-3">
            <label className="text-xs font-medium text-[#4F4F4D]">
              Extra context voor Emma <span className="text-[#A8A7A0]">(optioneel — geldt als expliciete projectwaarheid)</span>
            </label>
            <Textarea
              value={extraContext}
              onChange={(e) => setExtraContext(e.target.value)}
              placeholder="Bv. Behangpapier wordt geleverd door klant · Alleen één wand wordt behangen · Meubels worden door klant verplaatst"
              rows={2}
              className="text-sm rounded-xl"
              data-testid="ai-extra-context"
            />
          </div>

          {/* Actie-knoppen */}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={doGenerate}
              disabled={busyGen}
              className="bg-[#B07A4F] hover:bg-[#8f6440] text-white rounded-xl h-10"
              data-testid="ai-generate-btn"
            >
              {busyGen ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {busyGen ? "Emma denkt na…" : (gen ? "Regenereer" : "Genereer projectomschrijving")}
            </Button>
            {SHOW_EMMA_CHECK_UI && (
            <Button
              type="button"
              onClick={doCheck}
              disabled={busyCheck}
              variant="outline"
              className="rounded-xl h-10 border-[#B07A4F] text-[#B07A4F] hover:bg-[#FFF3E5]"
              data-testid="ai-check-btn"
            >
              {busyCheck ? <RefreshCw className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
              {busyCheck ? "Aan het meelezen…" : "Emma kijkt mee"}
            </Button>
            )}
            {gen && (
              <Button
                type="button"
                onClick={() => { setGen(null); setAccepted({}); setGenFingerprint(null); }}
                variant="ghost"
                className="rounded-xl h-10 text-[#6E6E6C]"
                data-testid="ai-cancel-preview-btn"
              >
                <X className="w-4 h-4" /> Sluit preview
              </Button>
            )}
          </div>

          {/* v14g — Subtiele stale-notice: alleen tonen na eerste generate
              wanneer offerte gewijzigd is. Geen automatische regeneratie. */}
          {isStale && (
            <button
              type="button"
              onClick={doGenerate}
              className="w-full text-left text-xs bg-[#FFF8ED] border border-[#F2DCB2] text-[#8B5E3C] rounded-lg px-3 py-2 hover:bg-[#FFF3E5] flex items-center gap-2"
              data-testid="ai-stale-notice"
            >
              <RefreshCw className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="flex-1">Offerte gewijzigd — <u>projectomschrijving opnieuw genereren</u></span>
            </button>
          )}

          {genErr && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              Fout bij genereren: {genErr}
            </div>
          )}

          {/* Preview */}
          {gen && !genErr && (
            <div className="border border-[#E5E2DC] bg-white rounded-xl p-3 space-y-3" data-testid="ai-preview">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex flex-col">
                  <span className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#5D7259]">
                    {gen._seeded_from_quote ? "Tijdelijke werkversie" : "Voorbeeld"} — accepteer per veld of alles ineens
                  </span>
                  {gen._seeded_from_quote && (
                    <span className="text-xs text-[#8B5E3C] italic mt-0.5"
                      data-testid="ai-preview-draft-hint">
                      Tijdelijke werkversie op basis van je huidige offerte. Je echte
                      offerte-velden wijzigen pas na 'Overnemen' of 'Accepteer alles'.
                    </span>
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={acceptAll}
                  className="bg-[#5D7259] hover:bg-[#4a5c47] text-white text-xs h-8 rounded-lg"
                  data-testid="ai-accept-all-btn"
                >
                  <Check className="w-3.5 h-3.5" /> Accepteer alles
                </Button>
              </div>
              {["title", "description", "works_included", "remarks"].map((field) => {
                const labels = {
                  title: "Offertetitel",
                  description: "Korte omschrijving",
                  works_included: "De werken omvatten",
                  remarks: "Opmerkingen / uitsluitingen",
                };
                const val = gen[field] || "";
                if (!val) return null;
                // v14m.1 — Bij een van-quote-geseede draft: alleen velden
                // tonen waar de suggestie effectief iets heeft toegevoegd.
                // Ongewijzigde velden verstoppen we zodat de preview compact
                // blijft en het duidelijk is welke suggesties werden toegevoegd.
                if (gen._seeded_from_quote) {
                  const cur = String(currentFields?.[field] ?? "").trim();
                  const draft = String(val).trim();
                  if (cur === draft) return null;
                }
                const isAccepted = accepted[field];
                return (
                  <div key={field} className="border border-[#F0EFEA] rounded-lg p-2 space-y-1" data-testid={`ai-preview-${field}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-[#5D7259] font-semibold">
                        {labels[field]}
                      </span>
                      {isAccepted ? (
                        <span className="text-[10px] text-[#5D7259] font-semibold flex items-center gap-1">
                          <Check className="w-3 h-3" /> Overgenomen
                        </span>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => acceptField(field)}
                          variant="outline"
                          className="text-xs h-7 rounded-md border-[#5D7259] text-[#5D7259] hover:bg-[#F0F5EF]"
                          data-testid={`ai-accept-${field}`}
                        >
                          <Check className="w-3 h-3" /> Overnemen
                        </Button>
                      )}
                    </div>
                    <div className="text-xs text-[#2C2C2A] whitespace-pre-wrap bg-[#FAF9F6] rounded p-2" data-testid={`ai-preview-${field}-text`}>
                      {val}
                    </div>
                  </div>
                );
              })}
              <p className="text-xs text-[#6E6E6C] italic">
                Preview wijzigt niets aan de offerte. Klik "Overnemen" om een veld in te voegen (Undo blijft mogelijk).
              </p>
            </div>
          )}

          {SHOW_EMMA_CHECK_UI && chkErr && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              Fout bij controleren: {chkErr}
            </div>
          )}

          {/* Check results */}
          {SHOW_EMMA_CHECK_UI && chk && !chkErr && (
            <div className="border border-[#E5E2DC] bg-white rounded-xl p-3 space-y-2" data-testid="ai-check-results">
              <div className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#5D7259]">
                {(chk.warnings || []).length === 0
                  ? "Emma kijkt mee"
                  : `Emma vond ${(chk.warnings || []).length} ${(chk.warnings || []).length === 1 ? "punt" : "punten"} om even te bekijken`}
              </div>
              {(chk.warnings || []).length === 0 && (
                <div className="text-sm text-[#5D7259] italic" data-testid="ai-no-warnings">
                  Emma ziet geen aandachtspunten — deze offerte oogt in orde.
                </div>
              )}
              {(chk.warnings || []).map((w, i) => {
                const hasSuggestion = !!(w.suggested_text && w.target_field);
                const isApplied = !!appliedSug[i];
                // v14m.2 — Verwijder de proactieve 'alreadyIn' knop-verberging.
                // Dubbele tekst wordt binnen applySuggestion afgehandeld
                // (idempotent). Zo verdwijnt de Overnemen-knop nooit vroegtijdig
                // door een false-positive match op currentFields.
                return (
                <div
                  key={i}
                  className={`text-xs rounded-lg border px-2.5 py-2 space-y-1 ${SEVERITY_STYLE[w.severity] || SEVERITY_STYLE.info}`}
                  data-testid={`ai-warning-${i}`}
                >
                  {/* v14m-polish — Header-rij: label links, subtiele
                      Overnemen-knop rechts (zelfde outline-stijl als de
                      preview-blokken van 'Genereer projectomschrijving'). */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase font-bold">
                      {TYPE_LABEL[w.type] || w.type} · {SEVERITY_LABEL[w.severity] || w.severity}
                    </span>
                    {hasSuggestion && !isApplied && (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => setPreviewSug({ idx: i, warning: w })}
                        variant="outline"
                        className="text-xs h-7 rounded-md border-[#5D7259] text-[#5D7259] hover:bg-[#F0F5EF] gap-1"
                        data-testid={`ai-warning-apply-${i}`}
                      >
                        <Check className="w-3 h-3" /> Overnemen
                      </Button>
                    )}
                    {hasSuggestion && isApplied && (
                      <span className="text-[10px] text-[#5D7259] font-semibold flex items-center gap-1"
                        data-testid={`ai-warning-applied-${i}`}>
                        <Check className="w-3 h-3" />
                        Overgenomen
                      </span>
                    )}
                  </div>
                  <div>{w.message}</div>
                </div>
                );
              })}
              <p className="text-xs text-[#6E6E6C] italic">
                Emma wijzigt niets. Deze bedenkingen zijn enkel om even mee te denken.
              </p>
            </div>
          )}
        </div>
      )}

      {/* v14m — Preview-dialog voor overnemen suggestie. Toont WELK veld
          wordt aangepast en ALLEEN de toe te voegen zin (geen tekstmassa). */}
      <Dialog open={!!previewSug} onOpenChange={(v) => { if (!v) setPreviewSug(null); }}>
        <DialogContent className="max-w-md" data-testid="ai-suggestion-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#B07A4F]" />
              Suggestie overnemen
            </DialogTitle>
            <DialogDescription>
              {previewSug && (
                <>
                  Emma stelt voor om deze zin toe te voegen aan het veld{" "}
                  <span className="font-semibold text-[#2C2C2A]">
                    {FIELD_LABEL[previewSug.warning.target_field] || previewSug.warning.target_field}
                  </span>.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {previewSug && (
            <div className="space-y-3">
              <div className="border border-[#E5E2DC] bg-[#FAF9F6] rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-[#5D7259] font-semibold mb-1">
                  {(previewSug.warning.apply_mode || "append") === "replace"
                    ? "Wordt vervangen door"
                    : "Toe te voegen zin"}
                </div>
                <p className="text-sm text-[#2C2C2A] whitespace-pre-wrap"
                   data-testid="ai-suggestion-preview-text">
                  {previewSug.warning.suggested_text}
                </p>
              </div>
              <p className="text-[11px] text-[#6E6E6C] italic">
                Je bestaande tekst blijft behouden. Undo (↶) blijft mogelijk na overname.
              </p>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPreviewSug(null)}
                  className="rounded-xl h-10"
                  data-testid="ai-suggestion-cancel-btn"
                >
                  <X className="w-4 h-4" /> Annuleren
                </Button>
                <Button
                  type="button"
                  onClick={() => applySuggestion(previewSug.idx, previewSug.warning)}
                  className="bg-[#5D7259] hover:bg-[#4a5c47] text-white rounded-xl h-10"
                  data-testid="ai-suggestion-confirm-btn"
                >
                  <Check className="w-4 h-4" /> Bevestig & voeg toe
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
